import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../context/ThemeContext";
import galleryImages from "../data/gallery.json";

const ImagesGallery = () => {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<number | null>(null);

  const open = useCallback((i: number) => setLightbox(i), []);
  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(
    () =>
      setLightbox((p) => (p !== null ? (p + 1) % galleryImages.length : null)),
    [],
  );
  const prev = useCallback(
    () =>
      setLightbox((p) =>
        p !== null
          ? (p - 1 + galleryImages.length) % galleryImages.length
          : null,
      ),
    [],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, next, prev]);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] transition-colors duration-300">
      <HeaderComp />

      {/* Page Hero */}
      <div className="pt-20 pb-10 md:pt-24 md:pb-14 bg-[var(--section-alt-bg)] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-orange-400/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-semibold uppercase tracking-wider mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                {galleryImages.length} Photos
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-primary)] mb-3 gowun-batang">
                Training Gallery
              </h1>
              <p className="text-[var(--text-secondary)] max-w-xl text-base md:text-lg">
                Capturing moments of excellence and dedication from our shooting
                range
              </p>
            </div>

            {/* Back to Home */}
            <button
              onClick={() => navigate("/")}
              className="btn-secondary group self-start md:self-auto"
            >
              <svg
                className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                />
              </svg>
              Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
          {galleryImages.map((img, index) => (
            <div
              key={img.id}
              onClick={() => open(index)}
              className="group relative overflow-hidden rounded-lg cursor-pointer aspect-[3/4]"
            >
              <img
                src={img.imageUrl}
                alt={`Gallery ${img.id}`}
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                {/* Zoom icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center transform scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
                      />
                    </svg>
                  </div>
                </div>

                {/* Number */}
                <div className="absolute bottom-2 right-2 text-white/60 text-xs font-mono">
                  {String(index + 1).padStart(2, "0")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
          onClick={close}
        >
          {/* Close */}
          <button
            onClick={close}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-200 z-10"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 md:top-6 left-4 md:left-6 flex items-center gap-2 text-white/70 text-sm font-mono z-10">
            <span className="text-white font-semibold">
              {String(lightbox + 1).padStart(2, "0")}
            </span>
            <span className="text-white/30">/</span>
            <span>{String(galleryImages.length).padStart(2, "0")}</span>
          </div>

          {/* Prev */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-200 z-10"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-200 z-10"
            aria-label="Next"
          >
            <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Image */}
          <img
            src={galleryImages[lightbox].imageUrl}
            alt={`Gallery ${galleryImages[lightbox].id}`}
            className="max-w-[90vw] max-h-[70vh] sm:max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Bottom Thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10 max-w-[90vw] overflow-x-auto py-2 px-2 scrollbar-hide">
            {galleryImages.map((img, index) => (
              <button
                key={img.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox(index);
                }}
                className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  index === lightbox
                    ? "border-orange-400 opacity-100 scale-105"
                    : "border-white/10 opacity-50 hover:opacity-80"
                }`}
              >
                <img
                  src={img.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesGallery;

/* ---- Shared Header ---- */
const HeaderComp = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const logo =
    "https://res.cloudinary.com/dbfyzgffj/image/upload/v1758905512/logo_zjf0g4.png";

  const navItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "Performance", action: () => navigate("/") },
    { label: "Gallery", action: () => navigate("/gallary") },
    { label: "Achievements", action: () => navigate("/") },
    { label: "Branches", action: () => navigate("/") },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[var(--bg-primary)]/90 backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.08)] border-b border-[var(--header-border)]"
            : "bg-gradient-to-b from-black/50 via-black/20 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-[84px]">
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group flex-shrink-0"
          >
            <div className="relative">
              <img
                src={logo}
                alt="OCSSC Logo"
                className="w-9 h-9 md:w-18 md:h-18 rounded-full transition-all duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 rounded-full ring-2 ring-orange-400/0 group-hover:ring-orange-400/60 transition-all duration-300" />
            </div>
            {/* Desktop */}
            <div className="hidden sm:flex flex-col items-start leading-none">
              <span
                className={`gowun-batang font-bold text-[22px] transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-primary)]" : "text-white"
                }`}
              >
                Orange City Sport Shooting Club
              </span>
              <span
                className={`text-[11px] font-medium tracking-[0.15em] uppercase mt-2 transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-secondary)]" : "text-white/60"
                }`}
              >
                Affiliated to Maharashtra Rifle Association
              </span>
            </div>
            {/* Mobile */}
            <div className="flex sm:hidden flex-col items-start leading-none">
              <span
                className={`gowun-batang font-bold text-[15px] transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-primary)]" : "text-white"
                }`}
              >
                Orange City Sport Shooting Club
              </span>
              <span
                className={`text-[8px] font-medium tracking-[0.12em] uppercase mt-1 transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-secondary)]" : "text-white/60"
                }`}
              >
                Affiliated to Maharashtra Rifle Association
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={item.action}
                className={`px-3 xl:px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                  scrolled
                    ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            {/* <button
              onClick={toggleTheme}
              className={`hidden md:flex p-2.5 rounded-xl transition-all duration-200 ${
                scrolled
                  ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Toggle theme"
            >
              {theme === "light" ? <MoonIcon className="w-[18px] h-[18px]" /> : <SunIcon className="w-[18px] h-[18px]" />}
            </button> */}

            {/* Phone CTA */}
            {/* <a
              href="tel:+917385035152"
              className={`hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                scrolled
                  ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                  : "bg-white/15 text-white border border-white/20 hover:bg-white/25 backdrop-blur-sm"
              }`}
            >
              <PhoneIcon className="w-3.5 h-3.5" />
              Contact
            </a> */}

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2.5 rounded-xl transition-all duration-200 ${
                scrolled
                  ? "text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <div className="flex flex-col gap-[5px]">
                  <span className="w-5 h-[1.5px] bg-current rounded-full" />
                  <span className="w-3.5 h-[1.5px] bg-current rounded-full" />
                  <span className="w-5 h-[1.5px] bg-current rounded-full" />
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-400 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />

        {/* Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[300px] max-w-[85vw] bg-[var(--bg-primary)] shadow-2xl transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 h-16 border-b border-[var(--border-light)]">
              <div className="flex items-center gap-2.5">
                {/* <img
                  src={logo}
                  alt="OCSSC Logo"
                  className="w-8 h-8 rounded-full"
                />
                <span className="gowun-batang font-bold text-sm text-[var(--text-primary)]">
                  Orange City Sport Shooting Club
                </span> */}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-all"
                aria-label="Close menu"
              >
                {/* <XMarkIcon className="w-5 h-5" /> */}
              </button>
            </div>

            {/* Nav Items */}
            <div className="flex-1 overflow-y-auto py-6 px-4">
              <nav className="flex flex-col gap-1">
                {navItems.map((item, i) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={`text-left px-4 py-3.5 rounded-xl font-medium transition-all duration-200 text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]`}
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <span className="flex items-center gap-3">
                      {item.label}
                    </span>
                  </button>
                ))}
              </nav>

              {/* Divider */}
              <div className="my-6 border-t border-[var(--border-light)]" />

              {/* Theme toggle */}
              {/* <button
                onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] font-medium transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center">
                  {theme === "light" ? <MoonIcon className="w-4 h-4" /> : <SunIcon className="w-4 h-4" />}
                </div>
                {theme === "light" ? "Dark Mode" : "Light Mode"}
              </button> */}
            </div>

            {/* Panel Footer */}
            {/* <div className="px-6 py-5 border-t border-[var(--border-light)]">
              <a
                href="tel:+917385035152"
                className="flex items-center justify-center gap-2.5 w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all duration-200 shadow-md shadow-orange-500/20"
              >
                <PhoneIcon className="w-4 h-4" />
                +91 7385035152
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};
